import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, emailFlows } from '../net/api';
import { Field } from './Field';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="center-screen">
      <div className="panel narrow">
        <a className="brand big-ish" href="/">Buddy Bay</a>
        {children}
      </div>
    </div>
  );
}

/** Landing page for the link in the confirmation email. */
export function VerifyEmailPage({ token }: { token: string }) {
  const [state, setState] = useState<{ kind: 'working' | 'ok' | 'err'; text?: string }>({ kind: 'working' });
  useEffect(() => {
    emailFlows.verify(token).then(
      (r) => setState({ kind: 'ok', text: `Thanks, ${r.username}! Your email is confirmed.` }),
      (e: Error) => setState({ kind: 'err', text: e.message }),
    );
  }, [token]);
  return (
    <Shell>
      <h2>Confirm your email</h2>
      {state.kind === 'working' && <p className="muted">Checking your link…</p>}
      {state.kind === 'ok' && <p className="note-ok">{state.text}</p>}
      {state.kind === 'err' && <p className="form-error">{state.text}</p>}
      <a className="btn wide" href="/">Go to Buddy Bay</a>
    </Shell>
  );
}

/** The links in email-change emails: confirm the new address, or "this wasn't me" from the old one. */
export function EmailLinkPage({ token, kind }: { token: string; kind: 'confirm' | 'undo' }) {
  const [state, setState] = useState<{ kind: 'working' | 'ok' | 'err'; text?: string }>({ kind: 'working' });
  useEffect(() => {
    (kind === 'confirm' ? emailFlows.confirmChange(token) : emailFlows.undoChange(token)).then(
      (r) =>
        setState({
          kind: 'ok',
          text:
            kind === 'confirm'
              ? `Done! ${r.username} now uses ${r.email}.`
              : `We put ${r.email} back on ${r.username} and signed the account out everywhere. Please sign in and change your password now.`,
        }),
      (e: Error) => setState({ kind: 'err', text: e.message }),
    );
  }, [token, kind]);
  return (
    <Shell>
      <h2>{kind === 'confirm' ? 'Confirm your new email' : 'Undo the email change'}</h2>
      {state.kind === 'working' && <p className="muted">Checking your link…</p>}
      {state.kind === 'ok' && <p className="note-ok">{state.text}</p>}
      {state.kind === 'err' && <p className="form-error">{state.text}</p>}
      <a className="btn wide" href="/">Go to Buddy Bay</a>
    </Shell>
  );
}

/** Landing page for the link in the password-reset email. */
export function ResetPasswordPage({ token }: { token: string }) {
  const [done, setDone] = useState<string>();
  const [error, setError] = useState<{ text: string; fields?: Record<string, string> }>();
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    if (f.get('password') !== f.get('confirm')) return setError({ text: "Those passwords don't match." });
    try {
      const r = await emailFlows.reset(token, String(f.get('password')));
      setDone(r.username);
    } catch (err) {
      setError({ text: (err as Error).message, fields: err instanceof ApiError ? err.fields : undefined });
    }
  }
  return (
    <Shell>
      <h2>Choose a new password</h2>
      {done ? (
        <>
          <p className="note-ok">Your password is changed, {done}. You've been signed out everywhere, so sign in again with the new one.</p>
          <a className="btn wide" href="/">Sign in</a>
        </>
      ) : (
        <form onSubmit={submit}>
          <Field id="np" name="password" label="New password (10+ characters)" type="password" autoComplete="new-password" required minLength={10} error={error?.fields?.password} />
          <Field id="np2" name="confirm" label="Type it again" type="password" autoComplete="new-password" required minLength={10} />
          {error && <p className="form-error" role="alert">{error.text}</p>}
          <button className="btn wide">Change password</button>
        </form>
      )}
    </Shell>
  );
}

export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      const r = await emailFlows.forgot(String(new FormData(e.currentTarget).get('email')));
      setMsg({ kind: 'ok', text: r.message });
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message });
    }
  }
  return (
    <section className="panel narrow">
      <h2>Forgot your password?</h2>
      <p className="muted small-print">Enter the email you signed up with and we'll send you a link to choose a new password.</p>
      <form onSubmit={submit}>
        <Field id="fp-email" name="email" label="Email" type="email" autoComplete="email" required />
        {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
        <button className="btn wide">Send reset link</button>
      </form>
      <button className="btn ghost" onClick={onBack}>Back to sign in</button>
    </section>
  );
}
